import { useCallback, useRef, useState, useMemo, useEffect } from 'react';

import {
  getElementWithMaxTextSize,
  Size,
} from '../utils/getElementWithMaxTextSize.js';

interface UseTickLabelSizeArgs {
  sizeProperty: Size;
}

export const useTickLabelSize = ({ sizeProperty }: UseTickLabelSizeArgs) => {
  const [maxTickLabelSize, setMaxTickLabelSize] = useState({
    text: '',
    width: 0,
  });
  const tickLabelsRefs = useRef<SVGTextElement[]>([]);
  const handleResizeOfTickLabels = useCallback(() => {
    const maxTickLabelSize = getElementWithMaxTextSize(
      tickLabelsRefs.current,
      sizeProperty,
    );

    setMaxTickLabelSize((prevMaxTickLabelSize) =>
      prevMaxTickLabelSize.text !== maxTickLabelSize.text ||
      prevMaxTickLabelSize.width !== maxTickLabelSize.width
        ? maxTickLabelSize
        : prevMaxTickLabelSize,
    );
  }, [sizeProperty]);

  const resizeObserver = useMemo(
    () => new ResizeObserver(handleResizeOfTickLabels),
    [handleResizeOfTickLabels],
  );

  const setTickLabelsRef = useCallback(
    (index: number) => (svgRef: SVGTextElement) => {
      if (svgRef) {
        if (
          tickLabelsRefs.current?.[index] &&
          svgRef !== tickLabelsRefs.current?.[index]
        ) {
          resizeObserver.unobserve(tickLabelsRefs.current?.[index]);
        }
        resizeObserver.observe(svgRef);
        tickLabelsRefs.current[index] = svgRef;
      }
    },
    [resizeObserver],
  );

  useEffect(() => {
    const prevTickLabels = tickLabelsRefs.current;

    return () => {
      prevTickLabels.forEach((tickLabelRef) =>
        resizeObserver.unobserve(tickLabelRef),
      );
      resizeObserver.disconnect();
    };
  }, [resizeObserver]);

  return { maxTickLabelSize, setTickLabelsRef };
};
